import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DraftPartyInput } from "@/types/drafting";

interface PartyConfirmationPanelProps {
  parties: DraftPartyInput[];
  onChange: (parties: DraftPartyInput[]) => void;
  onConfirm: () => void;
  isConfirming?: boolean;
  allConfirmed?: boolean;
}

export function PartyConfirmationPanel({
  parties,
  onChange,
  onConfirm,
  isConfirming = false,
  allConfirmed = false,
}: PartyConfirmationPanelProps) {
  function updateParty(index: number, patch: Partial<DraftPartyInput>) {
    const next = parties.map((party, i) => (i === index ? { ...party, ...patch } : party));
    onChange(next);
  }

  if (!parties.length) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--lm-border-subtle)] p-6 text-center dark:border-slate-700">
        <p className="text-sm text-[var(--lm-muted)] dark:text-slate-400">
          Paste party details or attach a party-source document, then extract parties.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {parties.map((party, index) => {
        const isSaved = party.confirmation_state === "confirmed";
        const hasUnsavedEdits = party.confirmation_state === "edited";
        return (
          <div
            key={`${party.label}-${index}`}
            className={cn(
              "rounded-xl border p-4",
              isSaved
                ? "border-green-500/30 bg-green-500/5 dark:border-green-500/30"
                : hasUnsavedEdits
                  ? "border-amber-500/30 bg-amber-500/5 dark:border-amber-500/30"
                  : "border-[var(--lm-border-card)] bg-white dark:border-slate-700 dark:bg-slate-900/45"
            )}
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                  {party.label}
                </h3>
                {typeof party.registration_data?.role === "string" && party.registration_data.role.trim() ? (
                  <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
                    {party.registration_data.role}
                  </p>
                ) : null}
              </div>
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                  isSaved
                    ? "bg-green-500/15 text-green-700 dark:text-green-300"
                    : hasUnsavedEdits
                      ? "bg-amber-500/15 text-amber-800 dark:text-amber-300"
                      : "bg-amber-500/15 text-amber-800 dark:text-amber-300"
                )}
              >
                {isSaved ? "Confirmed" : hasUnsavedEdits ? "Unsaved changes" : "Proposed"}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-[var(--lm-muted)] dark:text-slate-400">
                  Legal name
                </label>
                <Input
                  value={party.legal_name ?? ""}
                  onChange={(e) =>
                    updateParty(index, {
                      legal_name: e.target.value,
                      confirmation_state: "edited",
                    })
                  }
                  placeholder="Entity legal name"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-[var(--lm-muted)] dark:text-slate-400">
                  Entity type
                </label>
                <Input
                  value={party.entity_type ?? ""}
                  onChange={(e) =>
                    updateParty(index, {
                      entity_type: e.target.value,
                      confirmation_state: "edited",
                    })
                  }
                  placeholder="e.g. Corporation, LLC"
                />
              </div>
            </div>
          </div>
        );
      })}

      <Button
        type="button"
        variant="default"
        disabled={
          isConfirming ||
          (parties.length > 0 && parties.every((party) => party.confirmation_state === "confirmed"))
        }
        onClick={onConfirm}
        className="w-full sm:w-auto"
      >
        {isConfirming
          ? "Saving..."
          : parties.some((party) => party.confirmation_state === "edited")
            ? "Save party changes"
            : allConfirmed
              ? "Parties confirmed"
              : "Confirm parties"}
      </Button>
    </div>
  );
}
